import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Tag, CreditCard, Truck, Gift, ShoppingBag, Loader2, MapPin, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { calcBundle } from "@/lib/promo";
import { trackInitiateCheckout } from "@/lib/pixels";

const FREE_SHIP = 35;
const GIFT = 60;

export default function CartPage() {
  const { items, setQty, remove, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [guest, setGuest] = useState({ name: "", email: "" });
  const [ship, setShip] = useState({ name: "", phone: "", address: "", city: "", province: "", zip: "", country_code: "US" });
  const [loading, setLoading] = useState("");
  const [useCredits, setUseCredits] = useState(false);

  const { bundleDiscount, freeUnits, toNextFree } = calcBundle(items);
  const shipping = subtotal >= FREE_SHIP ? 0 : (subtotal > 0 ? 4.99 : 0);
  const discountAmt = discount?.discount || 0;
  const availableCredits = user?.credits || 0;
  const totalBeforeCredits = Math.max(0, subtotal - bundleDiscount - discountAmt) + shipping;
  const creditsApplied = useCredits ? Math.min(availableCredits, totalBeforeCredits) : 0;
  const total = Math.max(0, totalBeforeCredits - creditsApplied);
  const toGift = Math.max(0, GIFT - subtotal);

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/discount/validate", { code, subtotal });
      setDiscount(data);
      toast.success(`✅ ${data.label}`);
    } catch (e) {
      setDiscount(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const validGuest = user || (guest.email.includes("@") && guest.name.length > 1);
  const validShip = ship.name.length > 1 && ship.address.length > 3 && ship.city.length > 1 && ship.zip.length > 2;

  const pay = async (method) => {
    if (items.length === 0) return;
    if (!validGuest) { toast.error("Completa tu nombre y email para continuar"); return; }
    if (!validShip) { toast.error("Completa tu dirección de envío para continuar"); return; }
    trackInitiateCheckout(items, total);
    setLoading(method);
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      discount_code: discount?.code || null,
      origin_url: window.location.origin,
      email: user ? user.email : guest.email,
      name: user ? user.name : guest.name,
      shipping: { ...ship, name: ship.name || (user ? user.name : guest.name), country: "United States" },
      use_credits: useCredits,
    };
    try {
      if (method === "stripe") {
        const { data } = await api.post("/checkout/stripe", payload);
        window.location.href = data.url;
      } else {
        const { data } = await api.post("/checkout/binance", payload);
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading("");
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <h1 className="font-display font-black text-3xl uppercase">Tu carrito está vacío</h1>
        <p className="text-drelo-muted mt-2">Aún no agregaste nada. ¡Hay ofertas esperándote!</p>
        <Link to="/tienda" className="mt-6 inline-block bg-drelo-primary text-white font-black uppercase px-7 py-3 rounded-full hover:bg-drelo-hover">Ver ofertas</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-6 py-6 pb-28 lg:pb-6">
      <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mb-6">Finalizar compra</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Items + guest */}
        <div className="lg:col-span-2 space-y-4">
          {/* Unified unlock progress: $35 free shipping + $60 free gift */}
          <div className="bg-white border rounded-xl p-3.5">
            <div className="flex items-center justify-between text-sm font-bold mb-2.5">
              {subtotal >= GIFT ? (
                <span className="flex items-center gap-1.5 text-drelo-mint"><Gift className="w-4 h-4" /> ¡Envío gratis + regalo desbloqueados! 🎉</span>
              ) : subtotal >= FREE_SHIP ? (
                <span className="flex items-center gap-1.5"><Gift className="w-4 h-4 text-drelo-primary" /> ¡Envío gratis! Te faltan <span className="text-drelo-primary">${toGift.toFixed(2)}</span> para un regalo 🎁</span>
              ) : (
                <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-drelo-primary" /> Te faltan <span className="text-drelo-primary">${Math.max(0, FREE_SHIP - subtotal).toFixed(2)}</span> para envío gratis</span>
              )}
            </div>
            <div className="relative h-2 rounded-full bg-slate-100">
              <div
                className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-drelo-primary to-drelo-mint transition-all duration-500"
                style={{ width: `${Math.min(100, (subtotal / GIFT) * 100)}%` }}
              />
              {/* $35 free-shipping marker */}
              <div className="absolute -top-0.5" style={{ left: `${(FREE_SHIP / GIFT) * 100}%` }}>
                <div className={`w-3 h-3 -ml-1.5 rounded-full border-2 border-white ${subtotal >= FREE_SHIP ? "bg-drelo-mint" : "bg-slate-300"}`} />
              </div>
              {/* $60 free-gift marker (end) */}
              <div className="absolute -top-0.5 right-0">
                <div className={`w-3 h-3 -mr-0.5 rounded-full border-2 border-white ${subtotal >= GIFT ? "bg-drelo-mint" : "bg-slate-300"}`} />
              </div>
            </div>
            <div className="flex justify-between text-[11px] font-semibold text-drelo-muted mt-1.5">
              <span className={subtotal >= FREE_SHIP ? "text-drelo-mint" : ""}><Truck className="w-3 h-3 inline -mt-0.5" /> Envío gratis ${FREE_SHIP}</span>
              <span className={subtotal >= GIFT ? "text-drelo-mint" : ""}>Regalo 🎁 ${GIFT}</span>
            </div>
          </div>

          {/* Promo 3x2 banner */}
          <div className="bg-gradient-to-r from-drelo-primary to-drelo-deal text-white rounded-xl p-3 text-sm font-bold flex items-center gap-2" data-testid="bundle-banner">
            🎁 PROMO 3x2: {freeUnits > 0
              ? <span>¡Llevas {freeUnits} producto{freeUnits > 1 ? "s" : ""} GRATIS! (el más barato)</span>
              : <span>Añade {toNextFree} producto{toNextFree > 1 ? "s" : ""} más y el más barato es GRATIS</span>}
          </div>

          <div className="bg-white rounded-xl border divide-y">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-4 items-center p-4" data-testid={`checkout-item-${it.product_id}`}>
                <img src={it.image} alt={it.title} className="w-20 h-20 rounded-lg object-cover border" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold leading-tight line-clamp-2">{it.title}</p>
                  <p className="text-drelo-primary font-black mt-1">${it.price.toFixed(2)}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2 bg-slate-100 rounded-full px-1">
                    <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="p-1.5"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="font-bold w-6 text-center">{it.quantity}</span>
                    <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="p-1.5"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => remove(it.product_id)} className="text-drelo-muted hover:text-drelo-urgency"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Contact + shipping — single compact card */}
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold flex items-center gap-2"><MapPin className="w-4 h-4 text-drelo-primary" /> Datos y envío</h3>
              {!user && <Link to="/entrar" className="text-xs text-drelo-primary font-bold">Iniciar sesión</Link>}
            </div>

            {!user && (
              <div className="grid grid-cols-2 gap-2.5">
                <input value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="Nombre completo" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary" data-testid="guest-name" />
                <input value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="Email" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary" data-testid="guest-email" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2.5">
              <input value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} placeholder="Quien recibe" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary" data-testid="ship-name" />
              <input value={ship.phone} onChange={(e) => setShip({ ...ship, phone: e.target.value })} placeholder="Teléfono" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary" data-testid="ship-phone" />
              <input value={ship.address} onChange={(e) => setShip({ ...ship, address: e.target.value })} placeholder="Dirección (calle y número)" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary col-span-2" data-testid="ship-address" />
              <input value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} placeholder="Ciudad" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary col-span-2" data-testid="ship-city" />
              <input value={ship.province} onChange={(e) => setShip({ ...ship, province: e.target.value })} placeholder="Estado" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary" data-testid="ship-province" />
              <input value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} placeholder="ZIP" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary" data-testid="ship-zip" />
              <input value={ship.country_code} onChange={(e) => setShip({ ...ship, country_code: e.target.value.toUpperCase() })} placeholder="País" className="border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-drelo-primary col-span-2" data-testid="ship-country" />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border p-4 sticky top-28">
            <h3 className="font-bold mb-3">Resumen</h3>

            <div className="flex gap-2 mb-3">
              <div className="flex items-center flex-1 border rounded-lg px-3">
                <Tag className="w-4 h-4 text-drelo-muted" />
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Código de descuento" className="px-2 py-2.5 text-sm outline-none w-full uppercase" data-testid="discount-input" />
              </div>
              <button onClick={applyCode} className="bg-drelo-ink text-white px-4 rounded-lg font-bold text-sm" data-testid="apply-discount-btn">Aplicar</button>
            </div>
            <div className="text-[11px] text-drelo-muted mb-3">Prueba: <b>BIENVENIDA10</b> · <b>DRELO20</b></div>

            <div className="space-y-2 text-sm border-t pt-3">
              <div className="flex justify-between"><span className="text-drelo-muted">Subtotal</span><span className="font-bold">${subtotal.toFixed(2)}</span></div>
              {bundleDiscount > 0 && <div className="flex justify-between text-drelo-mint"><span>Promo 3x2 ({freeUnits} gratis)</span><span className="font-bold" data-testid="bundle-discount">-${bundleDiscount.toFixed(2)}</span></div>}
              {discountAmt > 0 && <div className="flex justify-between text-drelo-mint"><span>Descuento ({discount.code})</span><span className="font-bold">-${discountAmt.toFixed(2)}</span></div>}
              <div className="flex justify-between"><span className="text-drelo-muted flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Envío</span><span className="font-bold">{shipping === 0 ? <span className="text-drelo-mint">GRATIS</span> : `$${shipping.toFixed(2)}`}</span></div>
              {creditsApplied > 0 && <div className="flex justify-between text-drelo-mint"><span>Créditos aplicados</span><span className="font-bold" data-testid="credits-applied">-${creditsApplied.toFixed(2)}</span></div>}
              <div className="flex justify-between text-lg border-t pt-2"><span className="font-black">Total</span><span className="font-display font-black text-drelo-primary" data-testid="cart-total">${total.toFixed(2)}</span></div>
            </div>

            {user && availableCredits > 0 && (
              <label className="flex items-center gap-2 mt-3 text-sm bg-drelo-mint/10 border border-drelo-mint/30 rounded-lg px-3 py-2.5 cursor-pointer">
                <input type="checkbox" checked={useCredits} onChange={(e) => setUseCredits(e.target.checked)} data-testid="use-credits-checkbox" />
                <span>Usar mis créditos (<b>${availableCredits.toFixed(2)}</b> disponibles)</span>
              </label>
            )}

            <div className="mt-4 space-y-2.5">
              <button onClick={() => pay("stripe")} disabled={loading} className="w-full bg-drelo-primary text-white py-3.5 rounded-xl font-black uppercase tracking-wide hover:bg-drelo-hover active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-stripe-btn">
                {loading === "stripe" ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} Pagar con tarjeta
              </button>
            </div>
            <p className="text-[11px] text-center text-drelo-muted mt-3">🔒 Pago 100% seguro y encriptado</p>

            {/* Trust badges (reused from ProductDetail) */}
            <div className="grid grid-cols-3 gap-2 text-center text-[10px] mt-3 pt-3 border-t">
              {[[Truck, "Envío gratis +$35"], [ShieldCheck, "Pago seguro"], [RotateCcw, "Devolución 30 días"]].map(([Icon, t], i) => (
                <div key={i} className="flex flex-col items-center gap-1 text-drelo-muted"><Icon className="w-4 h-4 text-drelo-primary" /><span className="font-semibold leading-tight">{t}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky mobile pay bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t shadow-[0_-4px_20px_rgba(0,0,0,0.06)] px-4 py-3 flex items-center gap-3">
        <div className="leading-tight">
          <div className="text-[10px] uppercase font-bold text-drelo-muted tracking-wide">Total</div>
          <div className="font-display font-black text-lg text-drelo-primary" data-testid="sticky-total">${total.toFixed(2)}</div>
        </div>
        <button onClick={() => pay("stripe")} disabled={loading} className="flex-1 bg-drelo-primary text-white py-3 rounded-xl font-black uppercase tracking-wide hover:bg-drelo-hover active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-testid="sticky-checkout-stripe-btn">
          {loading === "stripe" ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} Pagar
        </button>
      </div>
    </div>
  );
}
